<script>
	import thumb_eIZpQpx0b0M from '$lib/Components/Youtube/images/eIZpQpx0b0M.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import HowWindowInsertsWork from '$lib/PageSections/HowWindowInsertsWork/HowWindowInsertsWork.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import Microphone from './images/microphone.jpg';
	import SoundProofingWindowInsert from './images/soundproof_window_insert.jpg';
</script>

<Section>
	<p>
		Home recording studio soundproofing is a continuous battle between noise
		creation and cancellation. If you are soundproofing a room for music and not
		adding recording studio windows, noise is coming in, and your sensitive
		equipment is going to pick that up. Soundproof studio windows stop noise
		before it enters your space.
	</p>
	<Image
		alt="indow window soundproofing a room for music"
		src={Microphone}
		class="w-full"
	/>
	<Bubble>
		{#snippet titleSlot()}
			<p class="mb-4 text-lg font-bold">Enter Indow® window inserts, which:</p>
		{/snippet}
		<ul>
			<li>Soundproof studio windows by reducing outside noise</li>
			<li>Let you be as loud as you want</li>
			<li>Don’t permanently modify your recording studio or home</li>
		</ul>
	</Bubble>
	<p>
		Soundproofing a room for music is easy with Indow inserts because you can
		install and remove them in minutes. Because they are laser measured to fit,
		each custom window insert fits perfectly, backed by our Snug Fit Guarantee.
		Once built, they just push into your window frame. Indow inserts provide
		soundproofing for a recording studio window by creating an insulating layer
		(via an extra layer of acrylic & a dead air space) against outside sound and
		temperatures.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h2>Recording in Silence</h2>
	<p>
		Ryan Bruce, owner of <a
			href="https://youtu.be/eIZpQpx0b0M"
			target="_blank"
			rel="external">Riffs, Bears, & Gear</a
		> YouTube channel needed soundproofing for his recording studio window. He put
		in one Indow window insert and it fixed a big problem he was facing:
	</p>
	<Youtube posterImageSrc={thumb_eIZpQpx0b0M} videoId="eIZpQpx0b0M" />
	<p class="text-lg text-indow-blue! italic">
		“The sound level in here is dead silent,” says Bruce. That’s not going to
		happen 100% of the time (you never know when a parade will come down your
		street), but these window inserts are going to greatly reduce the amount of
		noise coming into your home recording studio most of the time.”
	</p>
	<Button href="/how-to/get-indow-inserts">GET A FREE ESTIMATE</Button>
</Section>

<HowWindowInsertsWork
	title="Decibel Reduction & Soundproofing a Room for Recording"
>
	{#snippet description()}
		<div class="grid gap-4">
			<p>
				We’ve done <a href="/solutions/noise-performance">studies</a> on our
				<a href="/products/acoustic-window-inserts"
					>Acoustic Grade window inserts</a
				>. We know that our window inserts reduce outside noise up to 50% when
				placed with operable double-pane windows, and up to 70% when placed with
				operable single pane windows.* Similarly, our
				<a href="/products/acoustic-window-inserts">acoustic window inserts</a>
				reduce noise by 18.9
				<a href="/resources/faq/interior-storm-window-inserts-glossary#acoustic"
					>dBA</a
				>
				when used with single-pane windows and
				<a href="/products/thermal-window-inserts"
					>Indow Standard Grade window inserts</a
				>
				reduce noise by 10-12 dBA when used with single-pane windows.
			</p>
			<p>
				That can make a huge difference when using sensitive recording
				equipment.
			</p>
		</div>
	{/snippet}
</HowWindowInsertsWork>

<Section>
	<h2>Window Inserts + Sound Panels</h2>
	<p>
		As you could see in Bruce’s video, he had lots of sound panels already, but
		that didn’t stop his neighbor’s noise from coming right through his
		recording studio window. When you add home recording studio soundproofing,
		but don’t soundproof your recording studio windows, you are leaving a hole
		for sound to travel through. Sound panels +<a
			href="/products/acoustic-window-inserts"
		>
			soundproof window inserts</a
		>: it’s an acoustic treatment package.
	</p>
	<Image
		alt="indow window soundproofing a room for music"
		src={SoundProofingWindowInsert}
		class="w-full"
	/>
</Section>

<Section>
	<h2>Acrylic vs Glass</h2>
	<p>
		Indow window inserts are made from acrylic, which blocks sound better than
		standard window glass. Glass reverberates and reflects sound where acrylic
		blocks and absorbs. Soundproofing a room for recording means every surface
		area should be able to control reflection and reverberation of sound. So,
		when asking how to soundproof a room for music, know adding a layer of
		acrylic to the inside of your window helps to soundproof a window and with
		sound absorption throughout the room.
	</p>
	<Bubble>
		<p class="mb-8 text-lg font-bold">How to Soundproof a Room for Music</p>
		<ul>
			<li>Soundproof by blocking noise from entering</li>
			<li>Let light in while blocking sound - you can't record in the dark</li>
			<li>Don't seal out all the air - you still need to breath</li>
			<li>
				Stop reverberation and reflection of sound by covering all surfaces with
				sound-absorbing materials
			</li>
			<li>
				Don't forget the windows - windows reverberate sound and need to be
				covered in sound-absorbing material as well
			</li>
			<li>
				Keep testing and improving - every space is unique, find what works for
				yours
			</li>
		</ul>
	</Bubble>
	<p>
		Window inserts add a new layer of control to your recording environment:
		through noise reduction, temperature control, and a whole lot more. You can
		read and watch more about our <a href="/solutions/window-noise-reduction"
			>acoustic window inserts</a
		>
		and all <a href="/products/indowone-window-inserts">our window inserts</a>.
	</p>
	<p>
		Want to know more about how to soundproof a room for music? Connect with us
		for a <a href="/how-to/get-indow-inserts">free consultation</a>.
	</p>
</Section>

<Reviews />
<GetAFreeEstimate title="Let Us Help You" />
